<div class="backup-container">

  <!-- =========================
       HERO ENCABEZADO
  ========================== -->
  <section class="admin-hero">
    <div class="hero-text-block">
      <span class="eyebrow">SISTEMA & INFRAESTRUCTURA</span>
      <h1>Respaldos y Recuperación</h1>
      <p>
        Genera copias locales de la base de datos en formato JSON comprimido (.zip) o Microsoft Excel (.xlsx),
        y efectúa reversión de datos (rollback) ante pérdidas o contingencias operativas.
      </p>
    </div>

    <div class="hero-status">
      <span class="status-dot"></span>
      Base de Datos Activa
    </div>
  </section>

  <!-- =========================
       ALERTAS DE ESTADO
  ========================== -->
  @if (errorMessage) {
    <div class="alert alert-danger d-flex align-items-center" role="alert">
      <span class="material-symbols-outlined alert-icon me-2">error</span>
      <div class="flex-grow-1">{{ errorMessage }}</div>
      <button type="button" class="btn-close" (click)="errorMessage = null" aria-label="Cerrar"></button>
    </div>
  }

  @if (successMessage) {
    <div class="alert alert-success d-flex align-items-center" role="alert">
      <span class="material-symbols-outlined alert-icon me-2">check_circle</span>
      <div class="flex-grow-1">{{ successMessage }}</div>
      <button type="button" class="btn-close" (click)="successMessage = null" aria-label="Cerrar"></button>
    </div>
  }

  <!-- =========================
       ESTADO DE CARGA GLOBAL
  ========================== -->
  @if (isLoading) {
    <div class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <h3>Consultando base de datos</h3>
      <p>Verificando las colecciones institucionales en MongoDB Atlas...</p>
    </div>
  } @else {

    <!-- =========================
         MÉTRICAS VITALES (KPIS)
    ========================== -->
    <section class="metrics-grid">
      <article class="metric-card">
        <div class="metric-icon db-icon">
          <span class="material-symbols-outlined">database</span>
        </div>
        <div class="metric-content">
          <span class="metric-label">Estado de la Base de Datos</span>
          <strong class="metric-value text-success">Conectada</strong>
          <span class="metric-description">MongoDB Atlas vCore</span>
        </div>
      </article>

      <article class="metric-card">
        <div class="metric-icon collections-icon">
          <span class="material-symbols-outlined">folder_copy</span>
        </div>
        <div class="metric-content">
          <span class="metric-label">Colecciones Activas</span>
          <strong class="metric-value">
            {{ summary?.activeCollections ?? summary?.totalCollections ?? 0 }} / {{ summary?.totalCollections || 0 }}
          </strong>
          <span class="metric-description">Modelos autodescubiertos</span>
        </div>
      </article>

      <article class="metric-card">
        <div class="metric-icon documents-icon">
          <span class="material-symbols-outlined">receipt_long</span>
        </div>
        <div class="metric-content">
          <span class="metric-label">Total de Documentos</span>
          <strong class="metric-value">{{ summary?.totalDocuments || 0 }}</strong>
          <span class="metric-description">Registros en el sistema</span>
        </div>
      </article>

      <article class="metric-card">
        <div class="metric-icon rollback-icon">
          <span class="material-symbols-outlined">history</span>
        </div>
        <div class="metric-content">
          <span class="metric-label">Formato Oficial de Rollback</span>
          <strong class="metric-value">JSON (.zip)</strong>
          <span class="metric-description">Con manifiesto SHA-256</span>
        </div>
      </article>
    </section>

    <!-- =========================
         OPCIONES DE DESCARGA
    ========================== -->
    <section class="actions-section">
      <h2 class="section-title">
        <span class="material-symbols-outlined me-2">download</span>
        Generar y Descargar Copias Locales
      </h2>

      <div class="actions-grid">
        <!-- TARJETA: RESPALDO JSON ZIP -->
        <div class="action-card primary-action">
          <div class="action-card-header">
            <div class="action-badge-row">
              <span class="action-badge badge-official">OFICIAL PARA ROLLBACK</span>
              <span class="file-type-pill">.ZIP / JSON</span>
            </div>
            <h3>Respaldo Completo del Sistema</h3>
          </div>

          <p class="action-card-desc">
            Descarga un paquete comprimido que contiene los {{ summary?.totalCollections || 'todos los' }} archivos JSON correspondientes a cada modelo
            y el manifiesto criptográfico <code>manifest.json</code>. Este archivo contiene la estructura nativa exacta
            para restablecer el sistema en caso de rollback.
          </p>

          <div class="action-card-footer">
            <button
              class="btn btn-abaq-primary w-100"
              (click)="downloadJsonBackup()"
              [disabled]="isDownloadingJson"
            >
              @if (isDownloadingJson) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generando archivo ZIP...
              } @else {
                <span class="material-symbols-outlined me-2">archive</span>
                Descargar Respaldo JSON (.zip)
              }
            </button>
          </div>
        </div>

        <!-- TARJETA: EXPORTACIÓN EXCEL -->
        <div class="action-card excel-action">
          <div class="action-card-header">
            <div class="action-badge-row">
              <span class="action-badge badge-excel">AUDITORÍA Y CONSULTA</span>
              <span class="file-type-pill">.XLSX</span>
            </div>
            <h3>Libro Consolidado en Excel</h3>
          </div>

          <p class="action-card-desc">
            Genera un libro estructurado de Microsoft Excel con {{ summary?.totalCollections || 'múltiples' }} hojas de cálculo independientes con cabeceras institucionales
            formateadas, columnas autoajustadas y exclusión de contraseñas de usuarios. Ideal para auditorías, patronato y reportes administrativos.
          </p>

          <div class="action-card-footer">
            <button
              class="btn btn-abaq-success w-100"
              (click)="downloadExcelBackup()"
              [disabled]="isDownloadingExcel"
            >
              @if (isDownloadingExcel) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Construyendo libro Excel...
              } @else {
                <span class="material-symbols-outlined me-2">table_view</span>
                Descargar Base de Datos en Excel (.xlsx)
              }
            </button>
          </div>
        </div>

        <!-- TARJETA: CORTE Y DESHIDRATACIÓN (Fase D.2 - RA4-1) -->
        <div class="action-card dehydrate-action">
          <div class="action-card-header">
            <div class="action-badge-row">
              <span class="action-badge badge-dehydrate">CORTE HISTÓRICO</span>
              <span class="file-type-pill">.ZIP + PURGA</span>
            </div>
            <h3>Corte y Deshidratación</h3>
          </div>

          <p class="action-card-desc">
            Archiva y elimina de forma controlada los registros de colaboradores inactivos y oportunidades concluidas.
            El flujo es manual en 4 pasos: <strong>analizar</strong> el sistema, <strong>elegir</strong> una fecha disponible,
            <strong>descargar</strong> el respaldo del corte y, por último, <strong>borrar</strong> los registros.
          </p>

          <!-- AVISO DE ALTO CONSUMO -->
          <div class="dehydrate-warning">
            <span class="material-symbols-outlined me-2">bolt</span>
            <span>
              Operación de <strong>alto consumo</strong> de recursos. El análisis se ejecuta
              únicamente al pulsar <strong>Analizar</strong>, nunca de forma automática.
            </span>
          </div>

          <!-- PASO 1: ANALIZAR SISTEMA (aviso de estado integrado en el propio botón) -->
          <div class="dehydrate-analyze-step">
            <button
              type="button"
              data-testid="btn-analyze-dehydration"
              class="btn w-100 dehydrate-analyze-btn"
              [class.btn-abaq-primary]="analysisState === 'idle' || analysisState === 'analyzing'"
              [class.btn-analyze-found]="analysisState === 'found'"
              [class.btn-analyze-empty]="analysisState === 'empty'"
              (click)="analyzeDehydration()"
              [disabled]="isAnalyzingDehydration || isDownloadingDehydration || isPurgingDehydration"
            >
              @if (isAnalyzingDehydration) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Analizando sistema...
              } @else if (analysisState === 'found') {
                <span class="material-symbols-outlined me-2">check_circle</span>
                {{ availableCutoffs.length }} {{ availableCutoffs.length === 1 ? 'fecha disponible encontrada' : 'fechas disponibles encontradas' }} · Reanalizar
              } @else if (analysisState === 'empty') {
                <span class="material-symbols-outlined me-2">event_busy</span>
                No se encontraron fechas de corte disponibles
              } @else {
                <span class="material-symbols-outlined me-2">query_stats</span>
                Analizar sistema
              }
            </button>
          </div>

          <!-- PASO 2: DROPDOWN DE FECHAS DISPONIBLES -->
          <div class="dehydrate-field">
            <label for="cutoffDate" class="dehydrate-label">Fecha de corte disponible</label>
            <select
              id="cutoffDate"
              class="form-select dehydrate-date"
              [(ngModel)]="cutoffDate"
              (ngModelChange)="onCutoffDateChange()"
              [disabled]="availableCutoffs.length === 0 || isAnalyzingDehydration || isDownloadingDehydration || isPurgingDehydration"
            >
              <option value="">
                @if (analysisState === 'analyzing') {
                  Buscando fechas disponibles...
                } @else if (analysisState === 'empty') {
                  Sin fechas de corte disponibles
                } @else if (analysisState === 'found') {
                  Selecciona una fecha disponible ({{ availableCutoffs.length }})...
                } @else {
                  Primero pulsa Analizar sistema...
                }
              </option>
              @for (opt of availableCutoffs; track opt.cutoffDate) {
                <option [value]="opt.cutoffDate">{{ formatCutoffOption(opt) }}</option>
              }
            </select>
          </div>

          <!-- RESUMEN DE IMPACTO -->
          @if (dehydrationAnalysis) {
            <div class="dehydrate-impact animate-fade-in">
              <div class="dehydrate-impact-head">
                <span class="material-symbols-outlined text-primary-abaq">insights</span>
                <strong>Resumen de impacto</strong>
              </div>
              <p class="dehydrate-impact-total">
                Registros afectados por el corte:
                <strong>{{ dehydrationAnalysis.totalMatched }}</strong>
              </p>
              @if ((dehydrationAnalysis.retainedActivitiesCount || 0) > 0) {
                <p class="dehydrate-impact-retained">
                  Actividades compartidas retenidas en BD (con participantes activos/recientes):
                  <strong>{{ dehydrationAnalysis.retainedActivitiesCount }}</strong>
                </p>
              }
              @if (impactCollections.length) {
                <ul class="dehydrate-impact-list">
                  @for (col of impactCollections; track col.key) {
                    <li>
                      <span class="impact-name">{{ col.name || col.key }}</span>
                      <span class="impact-count">{{ col.matchedCount }}</span>
                    </li>
                  }
                </ul>
              }
            </div>
          }

          <div class="action-card-footer dehydrate-footer">
            <!-- PASO 3: DESCARGAR CORTE (emite purgeToken) -->
            <button
              class="btn btn-abaq-warning w-100"
              (click)="downloadDehydration()"
              [disabled]="!dehydrationAnalysis || isAnalyzingDehydration || isDownloadingDehydration || isPurgingDehydration"
            >
              @if (isDownloadingDehydration) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Descargando corte...
              } @else {
                <span class="material-symbols-outlined me-2">archive</span>
                Descargar corte (.zip + .txt)
              }
            </button>

            <!-- PASO 4: BORRAR REGISTROS (gated por purgeToken) -->
            <button
              class="btn btn-danger w-100"
              (click)="openPurgeConfirmModal()"
              [disabled]="!purgeToken || isPurgingDehydration"
            >
              @if (isPurgingDehydration) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Borrando registros...
              } @else {
                <span class="material-symbols-outlined me-2">delete_forever</span>
                Borrar registros
              }
            </button>

            @if (!dehydrationAnalysis) {
              <p class="dehydrate-gate-hint">
                Analiza el sistema y elige una fecha para habilitar la descarga.
              </p>
            } @else if (!purgeToken) {
              <p class="dehydrate-gate-hint">
                Descarga el corte para habilitar el borrado.
              </p>
            }
          </div>
        </div>
      </div>
    </section>

    <!-- =========================
         ZONA DE ROLLBACK / RESTAURACIÓN
    ========================== -->
    <section class="rollback-section">
      <div class="rollback-header">
        <h2 class="section-title text-danger-abaq">
          <span class="material-symbols-outlined me-2">settings_backup_restore</span>
          Restauración y Rollback del Sistema
        </h2>
        <span class="security-badge">ZONA SEGURA CON CONFIRMACIÓN EN 2 PASOS</span>
      </div>

      <div class="rollback-card">
        <div class="rollback-info-alert">
          <span class="material-symbols-outlined info-icon">info</span>
          <div>
            <strong>Procedimiento de recuperación:</strong>
            Sube un archivo de respaldo <code>.zip</code> generado previamente por este módulo. Primero se ejecutará una
            <strong>validación en seco (dry-run)</strong> que comprobará la integridad del manifiesto y te mostrará el balance
            de registros antes de realizar cualquier cambio en la base de datos.
          </div>
        </div>

        <!-- ZONA DE CARGA DE ARCHIVO -->
        <div class="upload-zone" [class.has-file]="!!selectedFile">
          @if (!selectedFile) {
            <div class="upload-drop-content">
              <span class="material-symbols-outlined upload-cloud-icon">upload_file</span>
              <h4>Selecciona el archivo de respaldo .zip</h4>
              <p>Haz clic para buscar en tu computadora el respaldo que deseas restaurar.</p>

              <label class="btn btn-outline-abaq btn-file-picker">
                <span class="material-symbols-outlined me-1">folder_open</span>
                Examinar archivo .zip
                <input
                  type="file"
                  accept=".zip,application/zip"
                  (change)="onFileSelected($event)"
                  hidden
                />
              </label>
            </div>
          } @else {
            <div class="selected-file-card">
              <div class="selected-file-info">
                <span class="material-symbols-outlined zip-file-icon">folder_zip</span>
                <div>
                  <strong class="selected-file-name">{{ selectedFile.name }}</strong>
                  <span class="selected-file-size text-muted">
                    {{ formatBytes(selectedFile.size) }}
                  </span>
                </div>
              </div>

              <div class="selected-file-actions">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  (click)="clearSelectedFile()"
                  [disabled]="isValidatingRollback || isExecutingRollback"
                >
                  <span class="material-symbols-outlined">close</span>
                  Cambiar archivo
                </button>

                @if (!dryRunReport) {
                  <button
                    class="btn btn-abaq-primary btn-sm ms-2"
                    (click)="validateRollback()"
                    [disabled]="isValidatingRollback"
                  >
                    @if (isValidatingRollback) {
                      <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                      Validando integridad...
                    } @else {
                      <span class="material-symbols-outlined me-1">verified</span>
                      Validar Respaldo (Dry-run)
                    }
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- RESULTADOS DEL DRY-RUN -->
        @if (dryRunReport) {
          <div class="dry-run-results animate-fade-in">
            <div class="dry-run-header">
              <div class="dry-run-title-group">
                <span class="material-symbols-outlined text-success">check_circle</span>
                <div>
                  <h4>Reporte de Validación Preliminar (Dry-Run)</h4>
                  <p class="text-muted">
                    Generado el: {{ dryRunReport.backupTimestamp | date:'medium' }} |
                    Versión: {{ dryRunReport.version }} |
                    Generado por: {{ dryRunReport.generatedBy }}
                  </p>
                </div>
              </div>

              <span class="badge bg-success-subtle text-success border border-success px-3 py-2">
                Integridad Criptográfica Aprobada
              </span>
            </div>

            <div class="table-responsive">
              <table class="table table-hover align-middle rollback-table">
                <thead>
                  <tr>
                    <th>Colección</th>
                    <th class="text-center">En Base de Datos Actual</th>
                    <th class="text-center">En Respaldo a Restaurar</th>
                    <th class="text-center">Variación</th>
                    <th class="text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  @for (col of dryRunReport.collections; track col.key) {
                    <tr>
                      <td>
                        <strong>{{ col.name }}</strong>
                        <span class="text-muted d-block small"><code>{{ col.key }}</code></span>
                      </td>
                      <td class="text-center font-monospace">{{ col.currentCount }}</td>
                      <td class="text-center font-monospace font-weight-bold">{{ col.backupCount }}</td>
                      <td class="text-center">
                        @if ((col.diff || 0) > 0) {
                          <span class="badge bg-success-subtle text-success">+{{ col.diff }}</span>
                        } @else if ((col.diff || 0) < 0) {
                          <span class="badge bg-danger-subtle text-danger">{{ col.diff }}</span>
                        } @else {
                          <span class="badge bg-secondary-subtle text-muted">Sin cambios (0)</span>
                        }
                      </td>
                      <td class="text-center">
                        @if (col.status === 'ready') {
                          <span class="badge bg-success text-white">Listo para restaurar</span>
                        } @else {
                          <span class="badge bg-warning text-dark">{{ col.status }}</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <!-- BOTÓN CRÍTICO DE RESTAURACIÓN -->
            <div class="dry-run-confirm-banner">
              <div>
                <h5>¿Deseas aplicar la reversión de datos?</h5>
                <p class="mb-0 text-muted">
                  Esta operación sobreescribirá las colecciones con los datos contenidos en el respaldo.
                </p>
              </div>

              <button
                class="btn btn-danger btn-lg shadow-sm"
                (click)="openConfirmModal()"
                [disabled]="isExecutingRollback"
              >
                <span class="material-symbols-outlined me-2">warning</span>
                Confirmar y Ejecutar Rollback
              </button>
            </div>
          </div>
        }

        <!-- RESULTADO DE ROLLBACK COMPLETADO -->
        @if (restoreResults && restoreResults.length > 0) {
          <div class="rollback-success-card animate-fade-in">
            <div class="success-card-header">
              <span class="material-symbols-outlined success-big-icon">task_alt</span>
              <div>
                <h4>Restauración de Base de Datos Exitosa</h4>
                <p>Las colecciones han sido sincronizadas y restablecidas con el respaldo proporcionado.</p>
              </div>
            </div>

            <div class="results-grid">
              @for (item of restoreResults; track item.key) {
                <div class="result-pill">
                  <span class="result-name">{{ item.name }}:</span>
                  <strong class="result-count">{{ item.restoredCount }} registros</strong>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </section>

    <!-- =========================
         ZONA DE REHIDRATACIÓN DE CORTES HISTÓRICOS
    ========================== -->
    <section class="rollback-section rehydration-section">
      <div class="rollback-header">
        <h2 class="section-title text-primary-abaq">
          <span class="material-symbols-outlined me-2">cloud_sync</span>
          Rehidratación de Cortes Históricos
        </h2>
        <span class="security-badge rehydration-badge">UPSERT IDEMPOTENTE CON DRY-RUN Y GOBERNANZA</span>
      </div>

      <div class="rollback-card rehydration-card">
        <div class="rollback-info-alert rehydration-info-alert">
          <span class="material-symbols-outlined info-icon">info</span>
          <div>
            <strong>Procedimiento de reincorporación de datos:</strong>
            Sube un archivo de corte <code>.zip</code> generado por el proceso de deshidratación. El sistema ejecutará una
            <strong>validación en seco (dry-run)</strong> verificando los sellos SHA-256 de cada colección, preservará las
            contraseñas originales y detectará posibles colisiones con usuarios activos antes de insertar cualquier registro.
          </div>
        </div>

        <!-- ZONA DE CARGA DE ARCHIVO DE CORTE -->
        <div class="upload-zone" [class.has-file]="!!selectedDehydrationFile">
          @if (!selectedDehydrationFile) {
            <div class="upload-idle">
              <span class="material-symbols-outlined upload-cloud-icon">cloud_upload</span>
              <p class="upload-prompt">Arrastra aquí el archivo <code>.zip</code> del corte o haz clic para seleccionarlo</p>
              <span class="upload-hint">Solo archivos comprimidos .zip con manifest.json (Opción A)</span>
              <input
                type="file"
                accept=".zip"
                class="file-input-hidden"
                id="dehydrationFileInput"
                (change)="onDehydrationFileSelected($event)"
              />
              <label for="dehydrationFileInput" class="btn btn-abaq-primary mt-3">
                <span class="material-symbols-outlined me-2">folder_open</span>
                Explorar Archivos
              </label>
            </div>
          } @else {
            <div class="file-selected-box">
              <div class="file-info-col">
                <span class="material-symbols-outlined file-zip-icon">archive</span>
                <div class="file-text">
                  <strong class="file-name">{{ selectedDehydrationFile.name }}</strong>
                  <span class="file-size">{{ formatBytes(selectedDehydrationFile.size) }}</span>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                (click)="clearSelectedDehydrationFile()"
                [disabled]="isValidatingDehydrationRestore || isExecutingDehydrationRestore"
              >
                <span class="material-symbols-outlined me-1">close</span>
                Remover
              </button>
            </div>
          }
        </div>

        <!-- ACCIONES DE VALIDACIÓN Y REHIDRATACIÓN -->
        @if (selectedDehydrationFile && !dehydrationDryRunReport && !dehydrationRestoreResult) {
          <div class="rollback-actions-bar">
            <button
              class="btn btn-abaq-primary"
              (click)="validateDehydrationRestore()"
              [disabled]="isValidatingDehydrationRestore"
            >
              @if (isValidatingDehydrationRestore) {
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verificando SHA-256 y colisiones...
              } @else {
                <span class="material-symbols-outlined me-2">fact_check</span>
                Validar Paquete de Corte (Dry-Run)
              }
            </button>
          </div>
        }

        <!-- REPORTE DE DRY-RUN DE REHIDRATACIÓN -->
        @if (dehydrationDryRunReport) {
          <div class="dry-run-report animate-fade-in">
            <div class="report-header">
              <div class="report-title-box">
                <span class="material-symbols-outlined text-success">verified</span>
                <div>
                  <h4>Integridad Verificada — Balance Preliminar del Corte</h4>
                  <p class="text-muted">
                    Total en paquete: <strong>{{ dehydrationDryRunReport.totals?.inPackage || 0 }}</strong> |
                    Restaurables: <strong class="text-success">{{ dehydrationDryRunReport.totals?.restorable || 0 }}</strong> |
                    Conflictos / Omitidos: <strong class="text-danger">{{ dehydrationDryRunReport.totals?.conflicts || 0 }}</strong> |
                    Ya en base de datos: <strong>{{ dehydrationDryRunReport.totals?.alreadyPresent || 0 }}</strong>
                  </p>
                </div>
              </div>
              <button
                class="btn btn-success btn-lg execute-rollback-btn"
                (click)="openDehydrationConfirmModal()"
                [disabled]="isExecutingDehydrationRestore || (dehydrationDryRunReport.totals?.restorable || 0) === 0"
              >
                <span class="material-symbols-outlined me-2">publish</span>
                Rehidratar {{ dehydrationDryRunReport.totals?.restorable || 0 }} Registros en BD
              </button>
            </div>

            @if ((dehydrationDryRunReport.conflicts || []).length > 0) {
              <div class="alert alert-warning mt-3">
                <span class="material-symbols-outlined me-2">warning</span>
                <div>
                  <strong>Registros con colisión detectados (serán omitidos para proteger datos activos):</strong>
                  <ul class="mb-0 mt-1">
                    @for (conf of dehydrationDryRunReport.conflicts; track conf.documentId) {
                      <li>
                        <strong>{{ conf.collection }}</strong> ({{ conf.field || conf.reason }}): {{ conf.value || conf.documentId }} — <em>{{ conf.reason }}</em>
                      </li>
                    }
                  </ul>
                </div>
              </div>
            }

            <div class="table-responsive mt-3">
              <table class="table dry-run-table">
                <thead>
                  <tr>
                    <th>Colección</th>
                    <th class="text-center">En Paquete</th>
                    <th class="text-center">Restaurables</th>
                    <th class="text-center">Conflictos</th>
                    <th class="text-center">Ya en BD</th>
                  </tr>
                </thead>
                <tbody>
                  @for (col of dehydrationDryRunReport.collections; track col.key) {
                    <tr>
                      <td>
                        <strong>{{ col.name }}</strong>
                        <span class="col-key">({{ col.key }})</span>
                      </td>
                      <td class="text-center">{{ col.inPackage }}</td>
                      <td class="text-center text-success font-weight-bold">{{ col.restorable }}</td>
                      <td class="text-center" [class.text-danger]="col.conflicts > 0">{{ col.conflicts }}</td>
                      <td class="text-center text-muted">{{ col.alreadyPresent }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        <!-- RESULTADOS FINALES TRAS REHIDRATAR -->
        @if (dehydrationRestoreResult) {
          <div class="restore-success-card animate-fade-in">
            <div class="success-head">
              <span class="material-symbols-outlined success-big-icon">check_circle</span>
              <div>
                <h3>¡Corte Rehidratado Exitosamente!</h3>
                <p>
                  Folio de auditoría: <code>{{ dehydrationRestoreResult.operationFolio }}</code>.
                  Se restablecieron {{ dehydrationRestoreResult.totalRestored }} registros sin alterar registros activos.
                </p>
              </div>
            </div>
          </div>
        }
      </div>
    </section>

  }

  <!-- =========================
       MODAL DE CONFIRMACIÓN CRÍTICA
  ========================== -->
  @if (showConfirmModal) {
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content confirm-modal-content">
          <div class="modal-header border-0 pb-0">
            <div class="danger-icon-circle">
              <span class="material-symbols-outlined">warning</span>
            </div>
            <button
              type="button"
              class="btn-close"
              (click)="closeConfirmModal()"
              aria-label="Cerrar"
            ></button>
          </div>

          <div class="modal-body text-center pt-2">
            <h3>¿Confirmas el Rollback de la Base de Datos?</h3>
            <p class="confirm-modal-warning">
              Estás a punto de reemplazar la información actual de las {{ summary?.totalCollections || '' }} colecciones con los datos
              del archivo <strong>{{ selectedFile?.name }}</strong>.
            </p>
            <div class="confirm-modal-note">
              Los registros actuales serán sobreescritos para asegurar la consistencia histórica.
            </div>
          </div>

          <div class="modal-footer border-0 justify-content-center gap-3 pt-0 pb-4">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              (click)="closeConfirmModal()"
              [disabled]="isExecutingRollback"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger px-4"
              (click)="executeRollback()"
              [disabled]="isExecutingRollback"
            >
              @if (isExecutingRollback) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Restaurando base de datos...
              } @else {
                <span class="material-symbols-outlined me-1">restore</span>
                Sí, ejecutar Rollback ahora
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- =========================
       MODAL DE CONFIRMACIÓN: BORRADO DEL CORTE (DESHIDRATACIÓN)
  ========================== -->
  @if (showPurgeConfirmModal) {
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content confirm-modal-content">
          <div class="modal-header border-0 pb-0">
            <div class="danger-icon-circle">
              <span class="material-symbols-outlined">delete_forever</span>
            </div>
            <button
              type="button"
              class="btn-close"
              (click)="closePurgeConfirmModal()"
              aria-label="Cerrar"
            ></button>
          </div>

          <div class="modal-body text-center pt-2">
            <h3>¿Borrar definitivamente los registros del corte?</h3>
            <p class="confirm-modal-warning">
              Se eliminarán de forma permanente los registros elegibles hasta
              <strong>{{ formatCutoffDateDisplay(cutoffDate) }}</strong>
              @if (dehydrationAnalysis) {
                (<strong>{{ dehydrationAnalysis.totalMatched }}</strong> registros).
              } @else {
                .
              }
            </p>
            <div class="confirm-modal-note">
              Asegúrate de haber descargado el corte. Esta acción no se puede deshacer.
            </div>
          </div>

          <div class="modal-footer border-0 justify-content-center gap-3 pt-0 pb-4">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              (click)="closePurgeConfirmModal()"
              [disabled]="isPurgingDehydration"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger px-4"
              (click)="purgeDehydration()"
              [disabled]="isPurgingDehydration || !purgeToken"
            >
              @if (isPurgingDehydration) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Borrando registros...
              } @else {
                <span class="material-symbols-outlined me-1">delete_forever</span>
                Sí, borrar registros ahora
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- =========================
       MODAL DE CONFIRMACIÓN REHIDRATACIÓN
  ========================== -->
  @if (showDehydrationConfirmModal) {
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content confirm-modal-content">
          <div class="modal-header border-0 pb-0">
            <div class="danger-icon-circle" style="background: rgba(37, 99, 235, 0.1); color: #2563eb;">
              <span class="material-symbols-outlined">cloud_sync</span>
            </div>
            <button
              type="button"
              class="btn-close"
              (click)="closeDehydrationConfirmModal()"
              aria-label="Cerrar"
            ></button>
          </div>

          <div class="modal-body text-center pt-2">
            <h3>¿Confirmas la rehidratación del corte en la base de datos?</h3>
            <p class="confirm-modal-warning">
              Se reincorporarán
              <strong>{{ dehydrationDryRunReport?.totals?.restorable || 0 }}</strong> registros archivados
              desde el archivo <strong>{{ selectedDehydrationFile?.name }}</strong>.
            </p>
            <div class="confirm-modal-note">
              Esta operación es idempotente: no alterará ningún colaborador ni registro activo del sistema.
            </div>
          </div>

          <div class="modal-footer border-0 justify-content-center gap-3 pt-0 pb-4">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              (click)="closeDehydrationConfirmModal()"
              [disabled]="isExecutingDehydrationRestore"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success px-4"
              (click)="executeDehydrationRestore()"
              [disabled]="isExecutingDehydrationRestore"
            >
              @if (isExecutingDehydrationRestore) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Rehidratando registros...
              } @else {
                <span class="material-symbols-outlined me-1">publish</span>
                Sí, rehidratar ahora
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }

</div>
